export interface DailyDua {
  day: number;
  arabic: string;
  transliteration: string;
  translation: string;
  source: string;
  type: 'prophetic' | 'quranic' | 'recommended';
  reflection: string;
}

export interface AllaahName {
  arabic: string;
  transliteration: string;
  meaning: string;
  reflection: string;
}

export interface RamadanPeriod {
  id: 1 | 2 | 3;
  name: string;
  arabicName: string;
  days: string;
  theme: string;
  description: string;
  blessing: string;
  affirmations: string[];
  reflectionQuestions: string[];
  namesOfAllah: AllaahName[];
  heartfeltDua: string[];
}

export interface LaylatulQadrContent {
  overview: string[];
  signs: string[];
  recommendedActs: {
    title: string;
    description: string;
    arabic?: string;
    transliteration?: string;
  }[];
  specialDua: {
    arabic: string;
    transliteration: string;
    translation: string;
    source: string;
    explanation: string;
  };
  nightlyFocus: Record<number, {
    focus: string;
    specialDua: string;
    reminder: string;
  }>;
}

// The 3 Periods of Ramadan
export const ramadanPeriods: RamadanPeriod[] = [
  {
    id: 1,
    name: "Days of Mercy",
    arabicName: "أيام الرحمة",
    days: "Days 1-10",
    theme: "Rahma - رحمة",
    description: "The first ten days of Ramadan are dedicated to seeking Allah's mercy. These are the days to cleanse the heart, set sincere intentions, and open ourselves to receive the boundless mercy of Ar-Rahman.",
    blessing: "The first ten days bring Allah's mercy upon those who seek it with sincerity.",
    affirmations: [
      "Allah is At-Tawwab, and He loves those who turn to Him in repentance",
      "I know that Allah's mercy encompasses all things",
      "With Allah's help, I can build lasting habits of worship",
      "Allah sees my sincere intention even when my actions are imperfect",
      "I trust that Allah will accept my efforts, however small",
      "Every blessing in my life is a gift from Allah",
      "I choose gratitude over complaint"
    ],
    reflectionQuestions: [
      "What does a 'new beginning' mean for me spiritually this Ramadan?",
      "What specific habits do I want to leave behind this month?",
      "How can I ensure my intentions remain pure throughout the month?",
      "What are three blessings I often overlook that I'm grateful for today?"
    ],
    namesOfAllah: [
      {
        arabic: "التَّوَّابُ",
        transliteration: "At-Tawwab",
        meaning: "The Ever-Returning, The Ever-Accepting of Repentance",
        reflection: "Allah turns to those who turn to Him. No matter how many times you've fallen, He is always ready to accept your return. This Ramadan is your fresh start because Allah is At-Tawwab."
      },
      {
        arabic: "الرَّحْمٰنُ",
        transliteration: "Ar-Rahman",
        meaning: "The Most Merciful, The Entirely Merciful",
        reflection: "Ar-Rahman's mercy is vast and all-encompassing. It embraces all of creation. In these first ten days, immerse yourself in seeking this boundless mercy."
      },
      {
        arabic: "الْكَرِيمُ",
        transliteration: "Al-Kareem",
        meaning: "The Most Generous, The Bountiful",
        reflection: "Allah gives without measure. He multiplies your good deeds and grants beyond what you deserve. Ask Al-Kareem generously."
      }
    ],
    heartfeltDua: [
      "Ya Allah, here I am at the beginning of this blessed month, and I come to You with a heart that wants to change. I come to You knowing that I've fallen short so many times, that I've made promises to myself and broken them, that I've let days and months slip by without truly turning to You the way I should.",
      "But Ya Allah, You know what's in my heart better than I know it myself. You know that despite my weakness, despite my forgetfulness, despite how many times I've stumbled—I want to be better. I want this Ramadan to be different.",
      "Ya Rabb, I'm asking You to help me make this month count. Not because I deserve it—I know I don't—but because You are Al-Kareem, the Most Generous, and You love to forgive.",
      "Please make my intentions pure. When I wake up for suhoor, when I stand in prayer, when I give charity, when I read Quran—let it all be for You alone.",
      "This is my new beginning, Ya Allah. Help me honor it. Ameen."
    ]
  },
  {
    id: 2,
    name: "Days of Forgiveness",
    arabicName: "أيام المغفرة",
    days: "Days 11-20",
    theme: "Maghfirah - مغفرة",
    description: "The middle ten days are for seeking forgiveness. This is the time to repent sincerely, let go of past mistakes, and ask Allah to cover our sins with His infinite forgiveness.",
    blessing: "The middle ten days bring Allah's forgiveness to those who repent with sincere hearts.",
    affirmations: [
      "Allah's forgiveness is always available to me",
      "I am not defined by my past mistakes",
      "Every moment is an opportunity to return to Allah",
      "I deserve to forgive myself as Allah forgives me",
      "Letting go of grudges frees my heart",
      "Allah forgives again and again, and so can I",
      "My repentance is accepted when it is sincere"
    ],
    reflectionQuestions: [
      "What sins am I carrying that I need to seek forgiveness for?",
      "Is there anyone I need to forgive—including myself?",
      "How can I make istighfar a daily habit beyond Ramadan?",
      "What would my life look like if I truly believed I was forgiven?"
    ],
    namesOfAllah: [
      {
        arabic: "الْغَفَّارُ",
        transliteration: "Al-Ghaffar",
        meaning: "The Repeatedly Forgiving",
        reflection: "Allah forgives again and again. No matter how many times you return to Him, Al-Ghaffar will continue to forgive. His forgiveness never runs out."
      },
      {
        arabic: "الْعَفُوُّ",
        transliteration: "Al-'Afuww",
        meaning: "The Pardoner, The Effacer of Sins",
        reflection: "Al-'Afuww doesn't just forgive—He erases sins as if they never happened. He wipes the slate completely clean. This is the Name to call upon in these days."
      },
      {
        arabic: "الرَّحِيمُ",
        transliteration: "Ar-Raheem",
        meaning: "The Most Merciful, The Bestower of Mercy",
        reflection: "Allah's mercy is specific and tender. He shows mercy to His servants in ways unique to their needs and circumstances."
      }
    ],
    heartfeltDua: [
      "Ya Allah, Ya Ghaffar, I come to You with a heavy heart—heavy with the weight of sins I remember and those I've forgotten. I've wronged myself so many times. I've wronged others. I've disobeyed You when I knew better.",
      "But here I am, in the middle of this blessed month, asking for Your forgiveness. Not because I deserve it, but because Your mercy is greater than my sins. Because You are Al-'Afuww, the One who pardons, and You love to pardon.",
      "Ya Allah, forgive me for the prayers I missed, the lies I told, the hearts I hurt, the time I wasted. Forgive me for the moments I chose my desires over Your commands.",
      "And Ya Rabb, help me forgive myself too. Help me let go of the guilt that chains me. Help me move forward as a better version of myself.",
      "Grant me the courage to seek forgiveness from those I've wronged, and soften my heart to forgive those who have wronged me. Ameen."
    ]
  },
  {
    id: 3,
    name: "Days of Salvation",
    arabicName: "أيام العتق من النار",
    days: "Days 21-30",
    theme: "Najat min an-Nar - النجاة من النار",
    description: "The final ten days are for seeking protection from the Hellfire. These blessed nights contain Laylatul Qadr, a night better than a thousand months. Intensify your worship and du'a.",
    blessing: "The last ten days bring salvation from the Hellfire and contain the Night of Power.",
    affirmations: [
      "I trust Allah's plan completely",
      "My du'as are always heard and answered in the best way",
      "I can maintain my spiritual growth after Ramadan",
      "Allah loves me and wants the best for me",
      "I am hopeful for what lies ahead",
      "Tonight could be Laylatul Qadr—I will strive with everything I have",
      "I place my trust in Al-Wakeel, the Disposer of Affairs"
    ],
    reflectionQuestions: [
      "What has been my biggest spiritual growth this Ramadan?",
      "What habits do I want to carry forward after Ramadan ends?",
      "How can I strengthen my tawakkul (trust in Allah) in daily life?",
      "What du'a do I want to make on Laylatul Qadr with all my heart?"
    ],
    namesOfAllah: [
      {
        arabic: "الْوَكِيلُ",
        transliteration: "Al-Wakeel",
        meaning: "The Trustee, The Disposer of Affairs",
        reflection: "When you put your trust in Al-Wakeel, He takes care of your affairs in the best way. Surrender your worries to Him as Ramadan comes to a close."
      },
      {
        arabic: "الْمُجِيبُ",
        transliteration: "Al-Mujeeb",
        meaning: "The Responsive, The Answerer of Prayers",
        reflection: "Allah hears every du'a and responds. Sometimes the answer is 'yes,' sometimes 'not yet,' sometimes 'I have something better.' Trust Al-Mujeeb's wisdom."
      },
      {
        arabic: "الْوَدُودُ",
        transliteration: "Al-Wadood",
        meaning: "The Most Loving, The Affectionate",
        reflection: "Allah loves His servants with a love that surpasses all human love. He wants good for you and delights in your worship."
      }
    ],
    heartfeltDua: [
      "Ya Allah, as Ramadan draws to a close, I feel a mix of emotions. Sadness that this blessed month is ending. Hope that You have accepted my fasting and prayers. Determination to carry what I've learned into the rest of the year.",
      "Ya Wakeel, I put my trust in You completely. Whatever happens after this month—in my life, my relationships, my work, my health—I know You are in control. Help me let go of anxiety and embrace tawakkul.",
      "Ya Allah, don't let this be the end. Let the habits I've built continue. Let the connection I've felt with You grow stronger. Let this Ramadan be a turning point, not just a temporary high.",
      "Grant me good in this world—good health, good company, good work, good character. And grant me good in the next—Your pleasure, Your Jannah, Your eternal mercy.",
      "As I search for Laylatul Qadr in these final nights, accept my du'as, Ya Allah. Accept my tears. Accept my efforts, however imperfect. And let me leave this Ramadan forgiven, renewed, and closer to You than ever before. Ameen."
    ]
  }
];

// 30 Days of Authentic Duas
export const dailyDuas: DailyDua[] = [
  // DAYS 1-10: MERCY (رحمة)
  {
    day: 1,
    arabic: "اللَّهُمَّ إِنِّي أَسْأَلُكَ عِلْمًا نَافِعًا، وَرِزْقًا طَيِّبًا، وَعَمَلًا مُتَقَبَّلًا",
    transliteration: "Allahumma inni as'aluka 'ilman nafi'an, wa rizqan tayyiban, wa 'amalan mutaqabbalan",
    translation: "O Allah, I ask You for beneficial knowledge, pure provision, and accepted deeds",
    source: "Ibn Majah",
    type: "prophetic",
    reflection: "Begin your Ramadan journey by asking for what truly matters: knowledge that benefits, provision that is pure, and deeds that are accepted."
  },
  {
    day: 2,
    arabic: "رَبِّ اغْفِرْ وَارْحَمْ وَأَنْتَ خَيْرُ الرَّاحِمِينَ",
    transliteration: "Rabbighfir warham wa anta khayrur-rahimeen",
    translation: "My Lord, forgive and have mercy, for You are the Best of those who show mercy",
    source: "Surah Al-Mu'minun, 23:118",
    type: "quranic",
    reflection: "Turn to Allah as the Best of those who show mercy. His mercy encompasses all things and awaits your sincere asking."
  },
  {
    day: 3,
    arabic: "اللَّهُمَّ أَعِنِّي عَلَى ذِكْرِكَ، وَشُكْرِكَ، وَحُسْنِ عِبَادَتِكَ",
    transliteration: "Allahumma a'inni 'ala dhikrika, wa shukrika, wa husni 'ibadatika",
    translation: "O Allah, help me to remember You, be grateful to You, and worship You in the best manner",
    source: "Abu Dawud",
    type: "prophetic",
    reflection: "The Prophet ﷺ taught this dua to Mu'adh ibn Jabal. Ask Allah's help in your worship—you cannot do it alone."
  },
  {
    day: 4,
    arabic: "رَبَّنَا لَا تُزِغْ قُلُوبَنَا بَعْدَ إِذْ هَدَيْتَنَا وَهَبْ لَنَا مِن لَّدُنكَ رَحْمَةً إِنَّكَ أَنتَ الْوَهَّابُ",
    transliteration: "Rabbana la tuzigh quloobana ba'da idh hadaytana wa hab lana min ladunka rahmatan innaka antal-Wahhab",
    translation: "Our Lord, do not let our hearts deviate after You have guided us, and grant us mercy from Yourself. Indeed, You are the Bestower",
    source: "Surah Ali 'Imran, 3:8",
    type: "quranic",
    reflection: "Guidance is a gift that must be protected. Ask Allah to keep your heart firm and to continue blessing you with His mercy."
  },
  {
    day: 5,
    arabic: "اللَّهُمَّ إِنِّي أَسْأَلُكَ الْهُدَى وَالتُّقَى وَالْعَفَافَ وَالْغِنَى",
    transliteration: "Allahumma inni as'alukal-huda wat-tuqa wal-'afafa wal-ghina",
    translation: "O Allah, I ask You for guidance, piety, chastity, and self-sufficiency",
    source: "Muslim",
    type: "prophetic",
    reflection: "These four qualities encompass spiritual and worldly success: guidance in matters of religion, piety in actions, modesty in conduct, and contentment in provision."
  },
  {
    day: 6,
    arabic: "رَبِّ أَوْزِعْنِي أَنْ أَشْكُرَ نِعْمَتَكَ الَّتِي أَنْعَمْتَ عَلَيَّ وَعَلَىٰ وَالِدَيَّ وَأَنْ أَعْمَلَ صَالِحًا تَرْضَاهُ",
    transliteration: "Rabbi awzi'ni an ashkura ni'matakal-lati an'amta 'alayya wa 'ala walidayya wa an a'mala salihan tardah",
    translation: "My Lord, enable me to be grateful for Your favor which You have bestowed upon me and upon my parents, and to do righteousness that pleases You",
    source: "Surah An-Naml, 27:19",
    type: "quranic",
    reflection: "Prophet Sulayman's dua teaches us to connect gratitude with righteous action—thanking Allah by doing what pleases Him."
  },
  {
    day: 7,
    arabic: "اللَّهُمَّ إِنِّي أَعُوذُ بِكَ مِنَ الْهَمِّ وَالْحَزَنِ، وَأَعُوذُ بِكَ مِنَ الْعَجْزِ وَالْكَسَلِ",
    transliteration: "Allahumma inni a'udhu bika minal-hammi wal-hazan, wa a'udhu bika minal-'ajzi wal-kasal",
    translation: "O Allah, I seek refuge in You from worry and grief, and I seek refuge in You from incapacity and laziness",
    source: "Bukhari",
    type: "prophetic",
    reflection: "The Prophet ﷺ regularly sought protection from anxiety about the future (worry) and sorrow about the past (grief). Free yourself through this dua."
  },
  {
    day: 8,
    arabic: "رَبَّنَا أَتْمِمْ لَنَا نُورَنَا وَاغْفِرْ لَنَا إِنَّكَ عَلَىٰ كُلِّ شَيْءٍ قَدِيرٌ",
    transliteration: "Rabbana atmim lana noorana waghfir lana innaka 'ala kulli shay'in qadeer",
    translation: "Our Lord, perfect our light for us and forgive us. Indeed, You are capable of all things",
    source: "Surah At-Tahrim, 66:8",
    type: "quranic",
    reflection: "On the Day of Judgment, the believers will ask for their light to be perfected. Ask for that light now—the light of faith and guidance."
  },
  {
    day: 9,
    arabic: "اللَّهُمَّ أَصْلِحْ لِي دِينِي الَّذِي هُوَ عِصْمَةُ أَمْرِي، وَأَصْلِحْ لِي دُنْيَايَ الَّتِي فِيهَا مَعَاشِي، وَأَصْلِحْ لِي آخِرَتِي الَّتِي فِيهَا مَعَادِي",
    transliteration: "Allahumma aslih li deeni alladhi huwa 'ismatu amri, wa aslih li dunyaya allati fiha ma'ashi, wa aslih li akhirati allati fiha ma'adi",
    translation: "O Allah, set right my religion which is the safeguard of my affairs, and set right my worldly life wherein is my livelihood, and set right my Hereafter to which is my return",
    source: "Muslim",
    type: "prophetic",
    reflection: "A comprehensive dua covering all aspects of life—your spiritual foundation, your worldly dealings, and your eternal destination."
  },
  {
    day: 10,
    arabic: "رَبَّنَا وَآتِنَا مَا وَعَدتَّنَا عَلَىٰ رُسُلِكَ وَلَا تُخْزِنَا يَوْمَ الْقِيَامَةِ إِنَّكَ لَا تُخْلِفُ الْمِيعَادَ",
    transliteration: "Rabbana wa atina ma wa'adtana 'ala rusulika wa la tukhzina yawmal-qiyamah innaka la tukhliful-mi'ad",
    translation: "Our Lord, grant us what You promised us through Your messengers and do not disgrace us on the Day of Resurrection. Indeed, You do not fail in Your promise",
    source: "Surah Ali 'Imran, 3:194",
    type: "quranic",
    reflection: "Close the first ten days by asking Allah to fulfill His promises to you and to protect you from disgrace on the Final Day."
  },

  // DAYS 11-20: FORGIVENESS (مغفرة)
  {
    day: 11,
    arabic: "رَبَّنَا ظَلَمْنَا أَنفُسَنَا وَإِن لَّمْ تَغْفِرْ لَنَا وَتَرْحَمْنَا لَنَكُونَنَّ مِنَ الْخَاسِرِينَ",
    transliteration: "Rabbana zalamna anfusana wa in lam taghfir lana wa tarhamna lanakunanna minal-khasireen",
    translation: "Our Lord, we have wronged ourselves, and if You do not forgive us and have mercy upon us, we will surely be among the losers",
    source: "Surah Al-A'raf, 7:23",
    type: "quranic",
    reflection: "The first words of repentance ever spoken by humans. Adam and Hawwa taught us to acknowledge our wrong, seek forgiveness, and depend on Allah's mercy."
  },
  {
    day: 12,
    arabic: "اللَّهُمَّ إِنَّكَ عَفُوٌّ تُحِبُّ الْعَفْوَ فَاعْفُ عَنِّي",
    transliteration: "Allahumma innaka 'Afuwwun tuhibbul-'afwa fa'fu 'anni",
    translation: "O Allah, You are the Pardoner, You love to pardon, so pardon me",
    source: "Tirmidhi",
    type: "prophetic",
    reflection: "The Prophet ﷺ taught this dua to Aisha for Laylatul Qadr. Al-'Afuww doesn't just forgive—He erases sins completely, as if they never happened."
  },
  {
    day: 13,
    arabic: "رَبِّ إِنِّي ظَلَمْتُ نَفْسِي فَاغْفِرْ لِي",
    transliteration: "Rabbi inni zalamtu nafsi faghfir li",
    translation: "My Lord, indeed I have wronged myself, so forgive me",
    source: "Surah Al-Qasas, 28:16",
    type: "quranic",
    reflection: "Prophet Musa's sincere confession when he made a mistake. Acknowledge your shortcomings honestly, and Allah's forgiveness will follow."
  },
  {
    day: 14,
    arabic: "اللَّهُمَّ اغْفِرْ لِي ذَنْبِي كُلَّهُ، دِقَّهُ وَجِلَّهُ، وَأَوَّلَهُ وَآخِرَهُ، وَعَلَانِيَتَهُ وَسِرَّهُ",
    transliteration: "Allahummaghfir li dhanbi kullahu, diqqahu wa jillahu, wa awwalahu wa akhirahu, wa 'alaniyatahu wa sirrahu",
    translation: "O Allah, forgive all my sins, the small and the great, the first and the last, the open and the secret",
    source: "Muslim",
    type: "prophetic",
    reflection: "A comprehensive plea for forgiveness covering every type of sin—nothing is left out. Let no sin remain unforgiven."
  },
  {
    day: 15,
    arabic: "رَّبِّ اغْفِرْ لِي وَلِوَالِدَيَّ وَلِمَن دَخَلَ بَيْتِيَ مُؤْمِنًا وَلِلْمُؤْمِنِينَ وَالْمُؤْمِنَاتِ",
    transliteration: "Rabbighfir li wa liwalidayya wa liman dakhala baytiya mu'minan wa lil-mu'mineena wal-mu'minat",
    translation: "My Lord, forgive me and my parents and whoever enters my home as a believer, and the believing men and women",
    source: "Surah Nuh, 71:28",
    type: "quranic",
    reflection: "Prophet Nuh's dua extends forgiveness to family and community. Seek forgiveness not just for yourself, but for all believers."
  },
  {
    day: 16,
    arabic: "اللَّهُمَّ إِنِّي أَسْتَغْفِرُكَ لِكُلِّ ذَنْبٍ يُعَقِّبُ الْحَسْرَةَ، وَيُورِثُ النَّدَامَةَ، وَيَحْبِسُ الرِّزْقَ، وَيَرُدُّ الدُّعَاءَ",
    transliteration: "Allahumma inni astaghfiruka li kulli dhanbin yu'aqqibul-hasrah, wa yurithun-nadamah, wa yahbisur-rizq, wa yaruddud-du'a",
    translation: "O Allah, I seek Your forgiveness for every sin that brings regret, inherits remorse, withholds provision, and rejects supplication",
    source: "Recommended",
    type: "recommended",
    reflection: "Some sins have lasting consequences—blocking blessings and making du'as feel unanswered. Seek forgiveness specifically for these."
  },
  {
    day: 17,
    arabic: "رَبَّنَا فَاغْفِرْ لَنَا ذُنُوبَنَا وَكَفِّرْ عَنَّا سَيِّئَاتِنَا وَتَوَفَّنَا مَعَ الْأَبْرَارِ",
    transliteration: "Rabbana faghfir lana dhunubana wa kaffir 'anna sayyi'atina wa tawaffana ma'al-abrar",
    translation: "Our Lord, forgive us our sins and remove from us our misdeeds, and cause us to die with the righteous",
    source: "Surah Ali 'Imran, 3:193",
    type: "quranic",
    reflection: "Ask not only for forgiveness but to die in the company of the righteous—to be counted among the best of Allah's servants."
  },
  {
    day: 18,
    arabic: "اللَّهُمَّ أَنْتَ رَبِّي لَا إِلَهَ إِلَّا أَنْتَ خَلَقْتَنِي وَأَنَا عَبْدُكَ، وَأَنَا عَلَى عَهْدِكَ وَوَعْدِكَ مَا اسْتَطَعْتُ",
    transliteration: "Allahumma anta Rabbi la ilaha illa anta, khalaqtani wa ana 'abduka, wa ana 'ala 'ahdika wa wa'dika mastata't",
    translation: "O Allah, You are my Lord, there is no god but You. You created me and I am Your slave, and I am upon Your covenant and promise as much as I am able",
    source: "Bukhari (Sayyid al-Istighfar)",
    type: "prophetic",
    reflection: "The master supplication for seeking forgiveness. The Prophet ﷺ said whoever recites it with conviction will enter Paradise."
  },
  {
    day: 19,
    arabic: "رَبَّنَا اغْفِرْ لَنَا وَلِإِخْوَانِنَا الَّذِينَ سَبَقُونَا بِالْإِيمَانِ وَلَا تَجْعَلْ فِي قُلُوبِنَا غِلًّا لِّلَّذِينَ آمَنُوا",
    transliteration: "Rabbanaghfir lana wa li-ikhwaninal-ladhina sabaquna bil-iman wa la taj'al fi qulubina ghillan lilladhina amanu",
    translation: "Our Lord, forgive us and our brothers who preceded us in faith and put not in our hearts any resentment toward those who believe",
    source: "Surah Al-Hashr, 59:10",
    type: "quranic",
    reflection: "True forgiveness includes purifying the heart of resentment. Ask Allah to remove any ill feelings toward fellow believers."
  },
  {
    day: 20,
    arabic: "رَبَّنَا آمَنَّا فَاغْفِرْ لَنَا وَارْحَمْنَا وَأَنتَ خَيْرُ الرَّاحِمِينَ",
    transliteration: "Rabbana amanna faghfir lana warhamna wa anta khayrur-rahimeen",
    translation: "Our Lord, we have believed, so forgive us and have mercy upon us, for You are the Best of the merciful",
    source: "Surah Al-Mu'minun, 23:109",
    type: "quranic",
    reflection: "Faith and forgiveness are connected. Declare your belief and ask for the forgiveness that comes with it."
  },

  // DAYS 21-30: SALVATION FROM HELLFIRE (النجاة من النار)
  {
    day: 21,
    arabic: "رَبَّنَا آتِنَا فِي الدُّنْيَا حَسَنَةً وَفِي الْآخِرَةِ حَسَنَةً وَقِنَا عَذَابَ النَّارِ",
    transliteration: "Rabbana atina fid-dunya hasanatan wa fil-akhirati hasanatan waqina 'adhaban-nar",
    translation: "Our Lord, give us good in this world and good in the Hereafter, and protect us from the punishment of the Fire",
    source: "Surah Al-Baqarah, 2:201",
    type: "quranic",
    reflection: "The most comprehensive dua—asking for good in both worlds and protection from the Fire. This was the Prophet's ﷺ most frequent dua."
  },
  {
    day: 22,
    arabic: "اللَّهُمَّ أَجِرْنِي مِنَ النَّارِ",
    transliteration: "Allahumma ajirni minan-nar",
    translation: "O Allah, protect me from the Fire",
    source: "Abu Dawud",
    type: "prophetic",
    reflection: "The Prophet ﷺ said whoever asks Allah for Paradise three times, Paradise says 'O Allah, enter him into Paradise,' and whoever seeks protection from the Fire three times, the Fire says 'O Allah, protect him from the Fire.'"
  },
  {
    day: 23,
    arabic: "رَبَّنَا اصْرِفْ عَنَّا عَذَابَ جَهَنَّمَ إِنَّ عَذَابَهَا كَانَ غَرَامًا",
    transliteration: "Rabbanas-rif 'anna 'adhaba jahannama inna 'adhabaha kana gharama",
    translation: "Our Lord, avert from us the punishment of Hell. Indeed, its punishment is an inseparable, permanent torment",
    source: "Surah Al-Furqan, 25:65",
    type: "quranic",
    reflection: "The servants of Ar-Rahman spend their nights in worship and their days seeking protection from the Fire. Be among them."
  },
  {
    day: 24,
    arabic: "اللَّهُمَّ إِنِّي أَسْأَلُكَ الْجَنَّةَ وَأَعُوذُ بِكَ مِنَ النَّارِ",
    transliteration: "Allahumma inni as'alukal-jannah wa a'udhu bika minan-nar",
    translation: "O Allah, I ask You for Paradise and seek refuge in You from the Fire",
    source: "Abu Dawud",
    type: "prophetic",
    reflection: "Simple yet profound. Ask for the ultimate reward and seek protection from the ultimate punishment."
  },
  {
    day: 25,
    arabic: "رَبَّنَا مَا خَلَقْتَ هَٰذَا بَاطِلًا سُبْحَانَكَ فَقِنَا عَذَابَ النَّارِ",
    transliteration: "Rabbana ma khalaqta hadha batilan subhanaka faqina 'adhaban-nar",
    translation: "Our Lord, You have not created this in vain. Glory be to You! So protect us from the punishment of the Fire",
    source: "Surah Ali 'Imran, 3:191",
    type: "quranic",
    reflection: "Those who reflect on creation recognize purpose and turn to Allah in worship. Contemplation leads to seeking protection."
  },
  {
    day: 26,
    arabic: "اللَّهُمَّ أَعْتِقْ رَقَبَتِي مِنَ النَّارِ",
    transliteration: "Allahumma a'tiq raqabati minan-nar",
    translation: "O Allah, free my neck from the Fire",
    source: "Ahmad",
    type: "prophetic",
    reflection: "In these final nights, Allah frees many souls from the Fire. Ask to be among those whose necks are freed."
  },
  {
    day: 27,
    arabic: "اللَّهُمَّ إِنَّكَ عَفُوٌّ كَرِيمٌ تُحِبُّ الْعَفْوَ فَاعْفُ عَنِّي",
    transliteration: "Allahumma innaka 'Afuwwun Kareemun tuhibbul-'afwa fa'fu 'anni",
    translation: "O Allah, You are the Pardoner, the Generous, You love to pardon, so pardon me",
    source: "Tirmidhi",
    type: "prophetic",
    reflection: "On what may be Laylatul Qadr, make this dua your constant companion. Al-'Afuww's pardon is complete and His generosity is endless."
  },
  {
    day: 28,
    arabic: "رَبَّنَا وَأَدْخِلْهُمْ جَنَّاتِ عَدْنٍ الَّتِي وَعَدتَّهُمْ وَمَن صَلَحَ مِنْ آبَائِهِمْ وَأَزْوَاجِهِمْ وَذُرِّيَّاتِهِمْ",
    transliteration: "Rabbana wa adkhilhum jannati 'adninil-lati wa'adtahum wa man salaha min aba'ihim wa azwajihim wa dhurriyyatihim",
    translation: "Our Lord, admit them to Gardens of Eternity which You have promised them, and whoever was righteous among their fathers, spouses, and descendants",
    source: "Surah Ghafir, 40:8",
    type: "quranic",
    reflection: "The angels make dua for the believers. Ask for Jannah not just for yourself, but for your family across generations."
  },
  {
    day: 29,
    arabic: "اللَّهُمَّ إِنِّي أَسْأَلُكَ الْفِرْدَوْسَ الْأَعْلَى مِنَ الْجَنَّةِ",
    transliteration: "Allahumma inni as'alukal-Firdawsal-A'la minal-Jannah",
    translation: "O Allah, I ask You for Al-Firdaws, the highest part of Paradise",
    source: "Bukhari",
    type: "prophetic",
    reflection: "The Prophet ﷺ taught us to ask for the best—Al-Firdaws, the highest level of Paradise, beneath the Throne of Allah."
  },
  {
    day: 30,
    arabic: "رَبَّنَا تَقَبَّلْ مِنَّا إِنَّكَ أَنتَ السَّمِيعُ الْعَلِيمُ وَتُبْ عَلَيْنَا إِنَّكَ أَنتَ التَّوَّابُ الرَّحِيمُ",
    transliteration: "Rabbana taqabbal minna innaka antas-Sami'ul-'Aleem. Wa tub 'alayna innaka antat-Tawwabur-Raheem",
    translation: "Our Lord, accept from us. Indeed, You are the All-Hearing, the All-Knowing. And turn to us in forgiveness. Indeed, You are the Accepting of repentance, the Merciful",
    source: "Surah Al-Baqarah, 2:127-128",
    type: "quranic",
    reflection: "End as Ibrahim and Ismail ended—asking for acceptance of your deeds. This is the dua of prophets who built the Ka'bah."
  }
];

// Laylatul Qadr Content
export const laylatulQadrContent: LaylatulQadrContent = {
  overview: [
    "Laylatul Qadr (The Night of Power/Decree) is the most blessed night of the year. It falls within the last ten nights of Ramadan, most likely on one of the odd nights (21st, 23rd, 25th, 27th, or 29th).",
    "Allah says in the Quran: 'The Night of Decree is better than a thousand months.' (97:3) This means worship on this single night is equivalent to over 83 years of worship.",
    "The Prophet ﷺ said: 'Whoever stands in prayer on Laylatul Qadr out of faith and seeking reward, his previous sins will be forgiven.' (Bukhari & Muslim)",
    "This is the night when the Quran was first revealed to Prophet Muhammad ﷺ, and the night when angels descend in abundance, led by Jibreel, bringing peace and blessings until Fajr."
  ],
  signs: [
    "The night is peaceful and tranquil, neither too hot nor too cold",
    "The moon appears like half a plate (not fully bright)",
    "The sun rises the next morning without rays, appearing pale",
    "Angels descend in great numbers, more than the pebbles on earth",
    "A sense of unusual peace and spiritual serenity",
    "Some scholars mention a slight drizzle or pleasant breeze"
  ],
  recommendedActs: [
    {
      title: "Night Prayer (Qiyam al-Layl)",
      description: "Whoever establishes the prayers on the night of Qadr out of sincere faith and hoping to attain Allah's rewards (not to show offf), then all his previous sins will be forgiven.",
      arabic: "مَن قَامَ لَيلَةَ القَدرِ إيمَانًا وَاحتِسَابًا، غُفِرَ لَهُ مَا تَقَدَّمَ مِن ذَنبِهِ",
      transliteration: "Man qama laylatal-qadri imanan wahtisaban, ghufira lahu ma taqaddama min dhanbihi"
    },
    {
      title: "Quran Recitation",
      description: "The Quran was revealed on this night. Increase your recitation, reflection, and connection with Allah's words."
    },
    {
      title: "Abundant Du'a",
      description: "Make extensive supplications for yourself, family, and the Ummah. Du'a is most accepted on this blessed night."
    },
    {
      title: "Istighfar (Seeking Forgiveness)",
      description: "Repent sincerely and frequently. Ask Allah to forgive your sins and the sins of your loved ones."
    },
    {
      title: "Dhikr and Tasbeeh",
      description: "Keep your tongue moist with the remembrance of Allah: SubhanAllah, Alhamdulillah, Allahu Akbar, La ilaha illAllah."
    },
    {
      title: "Charity",
      description: "Give in charity on these nights. The reward is multiplied beyond measure. Even a small amount given with sincerity is immense."
    },
    {
      title: "I'tikaf (Seclusion)",
      description: "If possible, spend the last ten nights in the masjid in worship and seclusion, following the Sunnah of the Prophet ﷺ."
    }
  ],
  specialDua: {
    arabic: "اللَّهُمَّ إِنَّكَ عَفُوٌّ كَرِيمٌ تُحِبُّ الْعَفْوَ فَاعْفُ عَنِّي",
    transliteration: "Allahumma innaka 'Afuwwun Kareemun tuhibbul-'afwa fa'fu 'anni",
    translation: "O Allah, You are the Pardoner, the Generous, You love to pardon, so pardon me",
    source: "Tirmidhi - taught by Prophet ﷺ to Aisha",
    explanation: "When Aisha asked the Prophet ﷺ what she should say if she knew which night was Laylatul Qadr, he taught her this beautiful dua. Al-'Afuww means the One who completely erases sins, as if they never existed. This is the dua to make your constant companion on these blessed nights."
  },
  nightlyFocus: {
    21: {
      focus: "Sincere Repentance",
      specialDua: "Begin the search for Laylatul Qadr with a clean heart. Repent for all sins—known and unknown.",
      reminder: "The Prophet ﷺ used to strive harder in worship during the last ten nights than at any other time."
    },
    23: {
      focus: "Gratitude & Praise",
      specialDua: "Thank Allah for allowing you to witness another Ramadan. Praise Him for His countless blessings.",
      reminder: "Angels are descending tonight. Fill the air with your dhikr and du'as."
    },
    25: {
      focus: "Dua for the Ummah",
      specialDua: "Expand your du'as beyond yourself. Pray for Muslims facing hardship around the world.",
      reminder: "Du'a made for others in their absence is always accepted, and an angel says 'Ameen, and the same for you.'"
    },
    27: {
      focus: "Personal Transformation",
      specialDua: "Many scholars consider this the most likely night. Pour out your heart. Ask for lasting change.",
      reminder: "This could be THE night. Worship as if your entire future depends on it—because it might."
    },
    29: {
      focus: "Completion & Acceptance",
      specialDua: "Ask Allah to accept your Ramadan. Pray for steadfastness after Ramadan ends.",
      reminder: "The last chance for Laylatul Qadr. Don't let fatigue stop you. Push through with everything you have."
    }
  }
};

// Helper functions
export const getDayDua = (day: number): DailyDua | undefined => {
  return dailyDuas.find(d => d.day === day);
};

export const getPeriodForDay = (day: number): RamadanPeriod | undefined => {
  if (day >= 1 && day <= 10) return ramadanPeriods[0];
  if (day >= 11 && day <= 20) return ramadanPeriods[1];
  if (day >= 21 && day <= 30) return ramadanPeriods[2];
  return undefined;
};

export const isOddNight = (day: number): boolean => {
  return [21, 23, 25, 27, 29].includes(day);
};

export const getLaylatulQadrNightFocus = (day: number) => {
  return laylatulQadrContent.nightlyFocus[day];
};
